import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { licensesAPI, customersAPI } from '../services/api';

const CreateLicense = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const preselectedCustomer = searchParams.get('customer');

  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({
    customer: preselectedCustomer || '',
    license_type: 'single',
    duration_days: 365,
    max_users: 1,
    max_activations: 1,
    notes: ''
  });

  useEffect(() => {
    fetchCustomers();
  }, []);

  const fetchCustomers = async () => {
    try {
      const response = await customersAPI.list();
      setCustomers(response.data.results || response.data);
    } catch (err) {
      console.error('Failed to fetch customers:', err);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm({ ...form, [name]: value });

    // Auto-set max_users based on license type
    if (name === 'license_type') {
      const maxUsers = {
        trial: 1,
        single: 1,
        team: 5,
        enterprise: 999
      };
      setForm(prev => ({ ...prev, [name]: value, max_users: maxUsers[value] || 1 }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await licensesAPI.create({
        ...form,
        duration_days: parseInt(form.duration_days),
        max_users: parseInt(form.max_users),
        max_activations: parseInt(form.max_activations)
      });
      navigate(`/dashboard/licenses/${response.data.id}`);
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to create license');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="page">
      <div className="page-header">
        <div>
          <h1 className="page-title">Create License</h1>
          <p className="page-description">Issue a new software license</p>
        </div>
      </div>

      <div className="card" style={{ maxWidth: '600px' }}>
        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label className="form-label">Customer *</label>
            <select
              name="customer"
              className="form-select"
              value={form.customer}
              onChange={handleChange}
              required
            >
              <option value="">Select a customer...</option>
              {customers.map((customer) => (
                <option key={customer.id} value={customer.id}>
                  {customer.company_name} ({customer.email})
                </option>
              ))}
            </select>
          </div>

          <div className="form-group">
            <label className="form-label">License Type *</label>
            <select
              name="license_type"
              className="form-select"
              value={form.license_type}
              onChange={handleChange}
              required
            >
              <option value="trial">Trial (14 days recommended)</option>
              <option value="single">Single User</option>
              <option value="team">Team (5 users)</option>
              <option value="enterprise">Enterprise (Unlimited)</option>
            </select>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
            <div className="form-group">
              <label className="form-label">Duration (days) *</label>
              <input
                type="number"
                name="duration_days"
                className="form-input"
                value={form.duration_days}
                onChange={handleChange}
                min="1"
                max="3650"
                required
              />
            </div>

            <div className="form-group">
              <label className="form-label">Max Users</label>
              <input
                type="number"
                name="max_users"
                className="form-input"
                value={form.max_users}
                onChange={handleChange}
                min="1"
              />
            </div>
          </div>

          <div className="form-group">
            <label className="form-label">Max Activations</label>
            <input
              type="number"
              name="max_activations"
              className="form-input"
              value={form.max_activations}
              onChange={handleChange}
              min="1"
              max="100"
            />
            <small style={{ color: 'var(--gray-500)', marginTop: '0.25rem', display: 'block' }}>
              Number of times this license can be activated on different machines
            </small>
          </div>

          <div className="form-group">
            <label className="form-label">Notes</label>
            <textarea
              name="notes"
              className="form-input"
              value={form.notes}
              onChange={handleChange}
              rows={3}
              placeholder="Internal notes about this license..."
            />
          </div>

          <div style={{ display: 'flex', gap: '0.5rem', marginTop: '1.5rem' }}>
            <button
              type="button"
              className="btn btn-secondary"
              onClick={() => navigate(-1)}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="btn btn-primary"
              disabled={loading}
            >
              {loading ? 'Creating...' : 'Create License'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CreateLicense;
